import { useNotification } from 'hooks/ui/useNotification';
import { useHistory } from 'react-router-dom';
import api from 'lib/trpcClient';
import { TRPCClientErrorLike } from '@trpc/client';
import { ApiRouter } from 'further-api/trpc/routes';
import { useQueryClient, QueryKey } from '@tanstack/react-query';

const useHandleTrpcMutation = <TData = unknown, TInput = unknown>() => {
  const { error, success } = useNotification();
  const history = useHistory();
  const apiUtils = api.useUtils();
  const queryClient = useQueryClient();

  type Args = {
    successMessage?: string;
    refetchType?: 'none' | 'all' | 'active' | 'inactive';
    successRedirect?: string;
    invalidations?: Array<QueryKey>;
    evictions?: Array<QueryKey>;
    onSuccess?: (data: TData) => void;
    onError?: (data: TRPCClientErrorLike<ApiRouter>) => void;
    onMutate?: (input: TInput) => void;
    onSettled?: (
      data: TData | undefined,
      error: TRPCClientErrorLike<ApiRouter> | null,
      input: TInput,
      context: unknown,
    ) => void;
  };

  return ({
    successMessage,
    successRedirect,
    onSuccess,
    onError,
    onMutate,
    onSettled,
    refetchType = 'all',
    invalidations,
    evictions,
  }: Args) => ({
    onSuccess: async (data: TData) => {
      if (successMessage) {
        success(successMessage);
      }

      if (evictions) {
        evictions.forEach((eviction) => {
          queryClient.removeQueries({ queryKey: eviction });
        });
      }

      // if there are no defined invalidations, then invalidate ALL queries in the cache
      if (!invalidations) {
        await apiUtils.invalidate(undefined, { refetchType });
      } else {
        // otherwise, invalidate only the defined queries
        await Promise.all(
          invalidations.map((invalidation) => {
            return queryClient.invalidateQueries({ queryKey: invalidation });
          }),
        );
      }

      onSuccess?.(data);

      if (successRedirect) {
        history.push(successRedirect);
      }
    },
    onError: (err: TRPCClientErrorLike<ApiRouter>) => {
      onError?.(err);
      error(err?.data?.cause ?? err?.message ?? 'Something went wrong.');
    },
    onMutate: (input: TInput) => {
      onMutate?.(input);
    },
    onSettled: (
      data: TData | undefined,
      err: TRPCClientErrorLike<ApiRouter> | null,
      input: TInput,
      context: unknown,
    ) => {
      onSettled?.(data, err, input, context);
    },
  });
};

export default useHandleTrpcMutation;
